(() => {
  'use strict';
  if (window.__ziqingCatalogGallery) {
    window.__ziqingCatalogGallery.init(document);
    return;
  }
  const instances = new WeakMap();
  const within = scope => [
    ...(scope.matches?.('[data-zcg]') ? [scope] : []),
    ...scope.querySelectorAll('[data-zcg]'),
  ];
  const imageURL = value => {
    try {
      if (!value) return '';
      const url = new URL(value, window.location.href);
      return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : '';
    } catch (_) { return ''; }
  };
  function initRoot(root) {
    if (instances.has(root)) return;
    const own = selector => [...root.querySelectorAll(selector)].filter(element => element.closest('[data-zcg]') === root);
    const link = own('[data-zcg-link]')[0];
    const frames = own('[data-zcg-frame]');
    const controls = own('[data-zcg-controls]')[0];
    const buttons = own('[data-zcg-select]');
    const feedback = own('[data-zcg-feedback]')[0];
    const message = own('[data-zcg-message]')[0];
    const retry = own('[data-zcg-retry]')[0];
    if (!link || !feedback || !message || !retry || ![1, 2].includes(frames.length)) return;
    const entries = frames.map((frame, index) => {
      const image = frame.querySelector(`[data-zcg-image="${index}"]`);
      return image && frame.dataset.zcgFrame === String(index) ? {
        frame, image, index, state: 'idle', attempt: 0, cancel: null,
        source: {
          src: image.getAttribute(index ? 'data-src' : 'src') || '',
          srcset: image.getAttribute(index ? 'data-srcset' : 'srcset') || '',
          sizes: image.getAttribute('sizes') || '',
        },
      } : null;
    });
    if (entries.some(entry => !entry || !imageURL(entry.source.src))) return;
    const twoImages = entries.length === 2;
    if (twoImages && (!controls || buttons.length !== 2 || buttons.some((button, index) => button.dataset.zcgSelect !== String(index))
      || imageURL(entries[0].source.src) === imageURL(entries[1].source.src))) return;
    const lifetime = new AbortController();
    const listen = (element, type, callback) => element.addEventListener(type, callback, { signal: lifetime.signal });
    const model = root.dataset.zcgModel || root.dataset.zcgProduct || 'Product';
    // Templates belong to this section instance, including editor reloads.
    // Replacement callbacks keep model text such as "$&" or "__INDEX__" literal.
    const translated = (value, fallback) => value && !/translation missing/i.test(value) ? value : fallback;
    const copy = {
      loading: translated(root.dataset.zcgLoading, 'Loading image __INDEX__ for __MODEL__…'),
      error: translated(root.dataset.zcgError, 'Image __INDEX__ for __MODEL__ could not be loaded. You can retry or use the image link.'),
      retry: translated(root.dataset.zcgRetryLabel, 'Retry image __INDEX__ for __MODEL__'),
    };
    const format = (template, index) => template.replace(/__MODEL__|__INDEX__/g,
      token => token === '__MODEL__' ? model : String(index + 1));
    let selected = 0;
    let target = 0;
    let shown = 0;
    let hovering = false;
    let destroyed = false;

    function clearFeedback() {
      feedback.hidden = true;
      message.textContent = '';
      retry.hidden = true;
      root.removeAttribute('aria-busy');
    }
    function paint() {
      if (destroyed) return;
      const entry = entries[target];
      if (entry.state === 'ready') {
        entries.forEach(item => { item.frame.hidden = item.index !== target; });
        shown = target;
        buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === shown)));
        clearFeedback();
      } else if (entry.state === 'loading') {
        // Keep the last displayed image in place while the requested one loads.
        feedback.hidden = false;
        message.textContent = format(copy.loading, target);
        retry.hidden = true;
        root.setAttribute('aria-busy', 'true');
      } else if (entry.state === 'error') {
        feedback.hidden = false;
        message.textContent = format(copy.error, target);
        retry.setAttribute('aria-label', format(copy.retry, target));
        retry.hidden = false;
        root.removeAttribute('aria-busy');
      }
    }
    function watch(entry, { start = false, replace = false, timeout = true } = {}) {
      entry.cancel?.();
      const attempt = ++entry.attempt;
      if (replace) {
        // A fresh element separates a failed attempt from delayed native events;
        // the currently displayed successful image is never replaced.
        const next = entry.image.cloneNode(false);
        next.removeAttribute('src');
        next.removeAttribute('srcset');
        entry.image.replaceWith(next);
        entry.image = next;
      }
      const image = entry.image;
      entry.state = 'loading';
      let timer = null;
      const current = () => !destroyed && entry.attempt === attempt && entry.image === image && entry.state === 'loading';
      const cancel = () => {
        if (timer !== null) window.clearTimeout(timer);
        image.removeEventListener('load', loaded);
        image.removeEventListener('error', failed);
      };
      const finish = state => {
        if (!current()) return;
        cancel();
        entry.state = state;
        if (target === entry.index) paint();
      };
      const loaded = () => { if (image.complete && image.naturalWidth > 0) finish('ready'); };
      const failed = () => { if (image.complete && image.naturalWidth === 0) finish('error'); };
      entry.cancel = cancel;
      image.addEventListener('load', loaded);
      image.addEventListener('error', failed);
      if (timeout) timer = window.setTimeout(() => finish('error'), 8000);
      if (start) {
        image.loading = 'eager';
        if (entry.source.sizes) image.setAttribute('sizes', entry.source.sizes);
        if (entry.source.srcset) image.setAttribute('srcset', entry.source.srcset);
        image.setAttribute('src', entry.source.src);
      }
      if (image.complete) image.naturalWidth > 0 ? loaded() : failed();
    }
    function choose(index) {
      if (!entries[index] || destroyed) return;
      target = index;
      const entry = entries[index];
      if (entry.state === 'idle') watch(entry, { start: true });
      paint();
    }
    if (twoImages) {
      buttons.forEach((button, index) => listen(button, 'click', () => { selected = index; choose(index); }));
      listen(link, 'pointerenter', event => {
        if (event.pointerType !== 'mouse' || !window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
        hovering = true;
        choose(1);
      });
      // Keep hover feedback reachable while moving from the image to Retry or
      // the dots. Restore the explicit choice only after leaving the component.
      listen(root, 'pointerleave', event => {
        if (event.pointerType !== 'mouse' || !hovering) return;
        hovering = false;
        choose(selected);
      });
      controls.hidden = false;
    }
    listen(retry, 'click', () => {
      const entry = entries[target];
      if (entry.state !== 'error') return;
      (buttons[target] || link).focus({ preventScroll: true });
      watch(entry, { start: true, replace: true });
      paint();
    });
    // Do not time out a lazy primary image before it has entered the viewport.
    watch(entries[0], { timeout: false });
    root.dataset.zcgReady = 'true';
    instances.set(root, () => {
      destroyed = true;
      lifetime.abort();
      entries.forEach(entry => { entry.attempt++; entry.cancel?.(); entry.frame.hidden = entry.index !== 0; });
      if (twoImages) {
        entries[1].image.removeAttribute('src');
        entries[1].image.removeAttribute('srcset');
        controls.hidden = true;
      }
      buttons.forEach((button, index) => button.setAttribute('aria-pressed', String(index === 0)));
      clearFeedback();
      delete root.dataset.zcgReady;
      instances.delete(root);
    });
  }
  const init = (scope = document) => within(scope).forEach(initRoot);
  window.__ziqingCatalogGallery = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => within(event.target).forEach(root => instances.get(root)?.()));
})();
